/**
 * Browser-side call recording. WhatsApp call media runs directly between the
 * agent's browser and WhatsApp, so the browser is the one place that hears
 * both sides - DoubleTick only records when they enable it for the
 * integration. The agent's mic and the customer's audio are mixed into one
 * track and captured as Opus/WebM.
 */

const MIME = 'audio/webm;codecs=opus';

export interface CallRecorder {
  /** Stop and resolve with the recording, or null when nothing was captured. */
  stop: () => Promise<Blob | null>;
}

/** Null when this browser cannot record Opus/WebM (e.g. Safari). */
export const startCallRecorder = (mic: MediaStream, remote: MediaStream): CallRecorder | null => {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported(MIME)) return null;

  const ctx = new AudioContext();
  const mix = ctx.createMediaStreamDestination();
  ctx.createMediaStreamSource(mic).connect(mix);
  ctx.createMediaStreamSource(remote).connect(mix);

  const recorder = new MediaRecorder(mix.stream, { mimeType: MIME, audioBitsPerSecond: 32_000 });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };
  // Timeslice so a crash mid-call still leaves most of the audio in memory.
  recorder.start(5_000);

  let stopped: Promise<Blob | null> | null = null;
  return {
    stop: () => {
      stopped ??= new Promise((resolve) => {
        recorder.onstop = () => {
          void ctx.close().catch(() => undefined);
          resolve(chunks.length ? new Blob(chunks, { type: 'audio/webm' }) : null);
        };
        if (recorder.state === 'inactive') recorder.onstop(new Event('stop'));
        else recorder.stop();
      });
      return stopped;
    },
  };
};
